/**
 * §9.2 Choose Your Enquiry — 5 interactive routing cards.
 */

import React from 'react'
import clsx from 'clsx'
import {
  Handshake, Calendar, LifeBuoy, Puzzle, Compass, Sparkles, ArrowRight, Check,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { ENQUIRY_TYPES } from '../data'
import { TONE } from '../_shared'

const ICONS = { Handshake, Calendar, LifeBuoy, Puzzle, Compass }

export default function Enquiry() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Choose Your Enquiry</Eyebrow>
          <SectionHeading align="center"
            title={<>Five paths.{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">One right team.</span></>}
            subtitle="Skip generic contact forms. Pick the path that matches where you are — sales, demo, support, partnerships or enterprise consulting — and land directly in front of the right desk."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4">
          {ENQUIRY_TYPES.map((e) => {
            const tone = TONE[e.tone] || TONE.orange
            const Icon = ICONS[e.iconKey] || Sparkles
            return (
              <a key={e.id} href={e.anchor}
                className={clsx(
                  'group relative rounded-2xl border p-5 backdrop-blur-xl overflow-hidden transition-all',
                  e.primary
                    ? 'border-brand-gold/40 bg-gradient-to-b from-brand-gold/[0.06] to-white/[0.01] shadow-[0_18px_50px_-25px_rgba(255,193,7,0.5)] hover:-translate-y-[2px]'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[2px]',
                )}>
                {e.primary && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full border border-brand-gold/40 bg-brand-gold/20 backdrop-blur-md px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-gold">
                    <Sparkles className="h-3 w-3" /> Most common
                  </span>
                )}
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 45% at 20% 0%, rgba(${tone.rgb},0.14), transparent 70%)` }} />
                <div className="relative flex flex-col gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <div>
                    <h4 className="text-[15px] font-semibold text-white leading-tight">{e.label}</h4>
                    <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-1.5 min-h-[42px]">{e.body}</p>
                  </div>
                  <ul className="flex flex-col gap-1">
                    {e.for.slice(0, 3).map((it) => (
                      <li key={it} className="flex items-start gap-2 text-[12px] text-zinc-300">
                        <Check className="h-3 w-3 mt-0.5 shrink-0" style={{ color: tone.hex }} />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 group-hover:text-brand-gold transition-colors mt-1">
                    {e.cta} <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </a>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
